<template>
    <div class="pendo-table-artifact">
        <filter-bar
            v-if="filters && Object.keys(filters).length > 0"
            :filters="filters" />
        <pendo-table
            ref="table"
            class="pendo-table-artifact"
            :auto-height-offset="32"
            :columns="tableColumns"
            :data="tableRows"
            :default-sort="getArtifactConfig(artifactId)?.sort"
            :pagination-config="{ enabled: tableRows.length > 50, initialPageSize: 50 }"
            :title="name"
            :max-height="isInline ? '500px' : undefined"
            csv-download
            manage-columns
            resizable
            @column-change="onColumnOrderChange"
            @sort-change="onSortChange" />
    </div>
</template>

<script>
import { PendoTable } from '@pendo/components';
import { mapActions, mapGetters } from 'vuex';
import keyBy from 'lodash/keyBy';
import difference from 'lodash/difference';
import FilterBar from '@/ask-pendo/components/filter-bar.vue';

export default {
    name: 'TableArtifact',
    components: {
        PendoTable,
        FilterBar,
    },
    props: {
        artifactId: {
            type: String,
            default: null,
        },
        data: {
            type: Object,
            default: null,
        },
        filters: {
            type: Object,
            default: null,
        },
        isInline: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: '',
        },
    },
    computed: {
        ...mapGetters({
            getArtifactConfig: 'askPendo/getArtifactConfig',
        }),
        tableColumns () {
            const allColumns = this.data.columns.map((column) => column.prop);
            const visibleColumnsOrder = this.getArtifactConfig(this.artifactId)?.columns;
            const remainingColumnsOrder = difference(allColumns, visibleColumnsOrder);

            const columnsByProp = keyBy(this.data.columns, 'prop');
            const toColumnObject = (visible) => (prop) => ({
                sortable: true,
                resizeable: true,
                visible,
                ...columnsByProp[prop],
            });

            // No column ordering/hiding has been set by the user so return all columns
            if (!visibleColumnsOrder) {
                return remainingColumnsOrder.map(toColumnObject(true));
            }

            return [
                ...visibleColumnsOrder.map(toColumnObject(true)),
                ...remainingColumnsOrder.map(toColumnObject(false)),
            ];
        },
        tableRows () {
            return this.data.rows.map((row, idx) => ({ id: idx + 1, ...row }));
        },
    },
    beforeUnmount () {
        this.clearArtifactMenuItems(this.artifactId);
    },
    mounted () {
        this.setArtifactMenuItems({
            artifactId: this.artifactId,
            items: [
                {
                    id: 'download-csv',
                    icon: 'download',
                    label: this.$t('Download CSV'),
                    onClick: () => {
                        this.$refs.table.downloadTableData?.();
                    },
                },
                {
                    id: 'manage-columns',
                    icon: 'columns',
                    label: this.$t('Manage columns'),
                    onClick: () => {
                        this.$refs.table.showManageColumnsModal?.();
                    },
                },
            ],
        });
    },
    methods: {
        ...mapActions('askPendo', ['clearArtifactMenuItems', 'setArtifactConfig', 'setArtifactMenuItems']),
        onColumnOrderChange ({ columns }) {
            this.setArtifactConfig({
                artifactId: this.artifactId,
                config: {
                    columns,
                },
            });
        },
        onSortChange ({ order, prop }) {
            this.setArtifactConfig({
                artifactId: this.artifactId,
                config: {
                    sort: {
                        order,
                        prop,
                    },
                },
            });
        },
    },
};
</script>

<style scoped lang="less">
.pendo-table-artifact {
    border: 0;

    :deep(.pendo-table__title) {
        display: none;
    }

    // Hide the table :after pseudo-element that shows up as a very annoying and difficult to find extra bottom border
    :deep(.pendo-table:after) {
        background-color: transparent;
    }
}

.event-search-artifact__best-label {
    margin-top: 0;
}

.event-search-artifact__additional-label {
    margin-top: 50px;
    color: var(--palette-gray-70);
}

.event-card {
    width: 100%;
}

.event-search-artifact__additional-matches {
    width: 100%;
    display: flex;
    gap: 20px;
}
</style>
